import { Flex, FlexProps } from '@chakra-ui/react';
import React, { FC } from 'react';
import styled from '@emotion/styled/macro';
import { rgba } from 'polished';

// Icons
import { ReactComponent as worldIcon } from 'assets/svgs/world.svg';
import { ReactComponent as sunIcon } from 'assets/svgs/sun.svg';
import { ReactComponent as bricksIcon } from 'assets/svgs/bricks.svg';
import { ReactComponent as bookmarkIcon } from 'assets/svgs/bookmark.svg';
import { ReactComponent as settingsIcon } from 'assets/svgs/settings.svg';
import { ReactComponent as userIcon } from 'assets/svgs/user.svg';
import { ReactComponent as userDeleteIcon } from 'assets/svgs/user-delete.svg';
import { ReactComponent as messageCircleIcon } from 'assets/svgs/message-circle.svg';
import { ReactComponent as listIcon } from 'assets/svgs/list.svg';
import { ReactComponent as moreIcon } from 'assets/svgs/more.svg';
import { ReactComponent as planeIcon } from 'assets/svgs/plane.svg';
import { ReactComponent as eventIcon } from 'assets/svgs/event.svg';
import { ReactComponent as carbonIcon } from 'assets/svgs/carbon.svg';
import { ReactComponent as closeIcon } from 'assets/svgs/close.svg';
import { ReactComponent as pulseIcon } from 'assets/svgs/pulse.svg';
import { ReactComponent as waterIcon } from 'assets/svgs/water.svg';
import { ReactComponent as dropIcon } from 'assets/svgs/drop.svg';
import { ReactComponent as treeIcon } from 'assets/svgs/tree.svg';
import { ReactComponent as infinityIcon } from 'assets/svgs/infinity.svg';
import { ReactComponent as societyIcon } from 'assets/svgs/society.svg';
import { ReactComponent as otherIcon } from 'assets/svgs/other.svg';
import { ReactComponent as bulbIcon } from 'assets/svgs/icon-bulb.svg';
import { ReactComponent as avocadoIcon } from 'assets/svgs/icon-avocado.svg';
import { ReactComponent as coffeeIcon } from 'assets/svgs/icon-coffee.svg';
import { ReactComponent as gasIcon } from 'assets/svgs/icon-gas.svg';
import { ReactComponent as phoneIcon } from 'assets/svgs/icon-phone.svg';
import { ReactComponent as laptopIcon } from 'assets/svgs/icon-laptop.svg';
import { ReactComponent as petsIcon } from 'assets/svgs/pets.svg';
import { ReactComponent as carIcon } from 'assets/svgs/car_icon.svg';
import { ReactComponent as lifestyleIcon } from 'assets/svgs/monster_pink_icon.svg';
import { ReactComponent as giftIcon } from 'assets/svgs/gift.svg';

const IconWrapper = styled(Flex)(
  () => `
    border-radius: 50%;

    svg {
      width: 100%;
      height: 100%;
    }
  `,
);

export type Icon =
  | 'world'
  | 'sun'
  | 'bricks'
  | 'user'
  | 'user-delete'
  | 'bookmark'
  | 'message-circle'
  | 'settings'
  | 'more'
  | 'plane'
  | 'event'
  | 'list'
  | 'close'
  | 'water'
  | 'tree'
  | 'drop'
  | 'infinity'
  | 'carbon'
  | 'society'
  | 'other'
  | 'pulse'
  | 'avocado'
  | 'coffee'
  | 'gas'
  | 'laptop'
  | 'phone'
  | 'car'
  | 'lifestyle'
  | 'pets'
  | 'bulb'
  | 'gift';

interface CustomIconProps {
  name: Icon;
  color?: string;
  withBackground?: boolean;
  backgroundColor?: string;
  preventSVGFill?: boolean;
}

const CustomIcon: FC<FlexProps & CustomIconProps> = ({ name, color, withBackground = false, preventSVGFill = false, backgroundColor, ...props }) => {
  let Icon = worldIcon;
  let ariaLabel = '';

  switch (name) {
    case 'world':
      Icon = worldIcon;
      ariaLabel = 'world icon';
      break;
    case 'sun':
      Icon = sunIcon;
      ariaLabel = 'sun icon';
      break;
    case 'bricks':
      Icon = bricksIcon;
      ariaLabel = 'bricks icon';
      break;
    case 'message-circle':
      Icon = messageCircleIcon;
      ariaLabel = 'message icon';
      break;
    case 'user':
      Icon = userIcon;
      ariaLabel = 'user icon';
      break;
    case 'settings':
      Icon = settingsIcon;
      ariaLabel = 'settings icon';
      break;
    case 'bookmark':
      Icon = bookmarkIcon;
      ariaLabel = 'bookmark icon';
      break;
    case 'more':
      Icon = moreIcon;
      ariaLabel = 'more icon';
      break;
    case 'plane':
      Icon = planeIcon;
      ariaLabel = 'plane icon';
      break;
    case 'event':
      Icon = eventIcon;
      ariaLabel = 'event icon';
      break;
    case 'carbon':
      Icon = carbonIcon;
      ariaLabel = 'carbon icon';
      break;
    case 'list':
      Icon = listIcon;
      ariaLabel = 'list icon';
      break;
    case 'close':
      Icon = closeIcon;
      ariaLabel = 'close icon';
      break;
    case 'pulse':
      Icon = pulseIcon;
      ariaLabel = 'pulse icon';
      break;
    case 'water':
      Icon = waterIcon;
      ariaLabel = 'water icon';
      break;
    case 'drop':
      Icon = dropIcon;
      ariaLabel = 'drop icon';
      break;
    case 'infinity':
      Icon = infinityIcon;
      ariaLabel = 'infinity icon';
      break;
    case 'tree':
      Icon = treeIcon;
      ariaLabel = 'tree icon';
      break;
    case 'society':
      Icon = societyIcon;
      ariaLabel = 'society icon';
      break;
    case 'other':
      Icon = otherIcon;
      ariaLabel = 'other icon';
      break;
    case 'user-delete':
      Icon = userDeleteIcon;
      ariaLabel = 'delete profile icon';
      break;
    case 'bulb':
      Icon = bulbIcon;
      ariaLabel = 'lightbulb icon';
      break;
    case 'avocado':
      Icon = avocadoIcon;
      ariaLabel = 'avocado icon';
      break;
    case 'coffee':
      Icon = coffeeIcon;
      ariaLabel = 'coffee icon';
      break;
    case 'gas':
      Icon = gasIcon;
      ariaLabel = 'gas icon';
      break;
    case 'phone':
      Icon = phoneIcon;
      ariaLabel = 'phone icon';
      break;
    case 'laptop':
      Icon = laptopIcon;
      ariaLabel = 'laptop icon';
      break;
    case 'car':
      Icon = carIcon;
      ariaLabel = 'car icon';
      break;
    case 'lifestyle':
      Icon = lifestyleIcon;
      ariaLabel = 'lifestyle icon';
      break;
    case 'pets':
      Icon = petsIcon;
      ariaLabel = 'pets icon';
      break;
    case 'gift':
      Icon = giftIcon;
      ariaLabel = 'gift icon';
      break;
    default:
      Icon = worldIcon;
  }

  return (
    <IconWrapper
      w="28px"
      h="28px"
      p="5px"
      align="center"
      justify="center"
      sx={{
        bg: withBackground && (color || backgroundColor) ? rgba((backgroundColor || color) as string, backgroundColor ? 1 : 0.15) : 'transparent',
        '& svg': {
          fill: preventSVGFill ? 'transparent' : color ?? 'transparent',
        },
        '& svg path': {
          fill: color ? color : 'transparent',
        },
      }}
      {...props}
    >
      <Icon aria-label={ariaLabel} />
    </IconWrapper>
  );
};

export default CustomIcon;
